import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { InvestmentPlatform } from "../types";
import { investmentPlatformsAPI } from "../services/api";
import LoadingSkeleton from "../components/LoadingSkeleton";
import PageTransition from "../components/PageTransition";
import { Plus, Edit2, Trash2, Building2, Eye } from "lucide-react";

const InvestmentPlatforms: React.FC = () => {
  const [platforms, setPlatforms] = useState<InvestmentPlatform[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPlatform, setEditingPlatform] = useState<InvestmentPlatform | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    type: "broker" as "broker" | "stock_broker" | "bank" | "exchange" | "wallet" | "crypto_exchange" | "other",
    accountNumber: "",
    notes: "",
    walletBalance: 0,
  });

  useEffect(() => {
    fetchPlatforms();
  }, []);

  const fetchPlatforms = async () => {
    try {
      setLoading(true);
      setError(""); // Clear previous errors
      console.log("Fetching investment platforms...");
      const data = await investmentPlatformsAPI.getPlatforms();
      console.log("Platforms loaded successfully:", data);
      setPlatforms(data);
    } catch (err: any) {
      console.error("Error fetching platforms:", err);
      console.error("Error details:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
        config: err.config
      });
      const errorMessage = err.response?.data?.message || err.message || "Failed to load platforms";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError("Platform name is required");
      return;
    }

    try {
      setError("");
      if (editingPlatform) {
        console.log("Updating platform:", editingPlatform._id, formData);
        await investmentPlatformsAPI.updatePlatform(editingPlatform._id, {
          name: formData.name.trim(),
          type: formData.type,
          accountNumber: formData.accountNumber.trim(),
          notes: formData.notes.trim(),
          walletBalance: formData.walletBalance,
        });
      } else {
        console.log("Creating platform:", formData);
        await investmentPlatformsAPI.createPlatform({
          name: formData.name.trim(),
          type: formData.type,
          accountNumber: formData.accountNumber.trim(),
          notes: formData.notes.trim(),
          walletBalance: formData.walletBalance,
        });
      }
      resetForm();
      fetchPlatforms();
    } catch (err: any) {
      console.error("Error saving platform:", err);
      const errorMessage = err.response?.data?.message || err.message || "Failed to save platform";
      setError(errorMessage);
    }
  };

  const handleEdit = (platform: InvestmentPlatform) => {
    setEditingPlatform(platform);
    setFormData({
      name: platform.name,
      type: platform.type,
      accountNumber: platform.accountNumber || "",
      notes: platform.notes || "",
      walletBalance: platform.walletBalance || 0,
    });
    setShowAddForm(true);
  };

  const handleDelete = async (platformId: string) => {
    if (window.confirm("Are you sure you want to delete this platform? All investments will be removed.")) {
      try {
        await investmentPlatformsAPI.deletePlatform(platformId);
        fetchPlatforms();
      } catch (err: any) {
        setError(err.response?.data?.message || "Failed to delete platform");
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      type: "broker",
      accountNumber: "",
      notes: "",
      walletBalance: 0,
    });
    setShowAddForm(false);
    setEditingPlatform(null);
    setError("");
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      broker: "Broker",
      stock_broker: "Stock Broker",
      bank: "Bank",
      exchange: "Exchange",
      wallet: "Wallet",
      crypto_exchange: "Crypto Exchange",
      other: "Other",
    };
    return labels[type] || type;
  };

  const getTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      broker: "bg-blue-100 text-blue-700",
      stock_broker: "bg-indigo-100 text-indigo-700",
      bank: "bg-green-100 text-green-700",
      exchange: "bg-purple-100 text-purple-700",
      wallet: "bg-orange-100 text-orange-700",
      crypto_exchange: "bg-yellow-100 text-yellow-700",
      other: "bg-gray-100 text-gray-700",
    };
    return colors[type] || "bg-gray-100 text-gray-700";
  };

  if (loading) {
    return (
      <PageTransition>
        <div className="p-6 max-w-7xl mx-auto">
          <LoadingSkeleton variant="card" count={4} />
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Investment Platforms</h1>
            <p className="text-gray-600 mt-2">Manage your investment accounts and platforms</p>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-xl"
          >
            <Plus className="w-5 h-5" />
            Add Platform
          </button>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl">
            {error}
          </div>
        )}

        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="mb-8 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingPlatform ? "Edit Platform" : "Add New Platform"}
              </h3>
            </div>
            <div className="p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Platform Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="e.g., Zerodha, Groww, Upstox"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Platform Type *
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="broker">Broker</option>
                      <option value="stock_broker">Stock Broker</option>
                      <option value="bank">Bank</option>
                      <option value="exchange">Exchange</option>
                      <option value="crypto_exchange">Crypto Exchange</option>
                      <option value="wallet">Wallet</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Account Number
                    </label>
                    <input
                      type="text"
                      value={formData.accountNumber}
                      onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Your account number"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Wallet Balance
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.walletBalance}
                      onChange={(e) => setFormData({ ...formData, walletBalance: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Additional notes about this platform..."
                  />
                </div>

                <div className="flex gap-4">
                  <button
                    type="submit"
                    className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-colors"
                  >
                    {editingPlatform ? "Update Platform" : "Add Platform"}
                  </button>
                  <button
                    type="button"
                    onClick={resetForm}
                    className="bg-gray-200 text-gray-800 px-6 py-3 rounded-xl hover:bg-gray-300 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Platforms Grid */}
        {platforms.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl shadow-lg border border-gray-100">
            <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No platforms yet</h3>
            <p className="text-gray-600 mb-6">Start by adding your first investment platform</p>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-colors"
            >
              Add Platform
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {platforms.map((platform) => (
              <div
                key={platform._id}
                className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-200"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{platform.name}</h3>
                        <span className={`inline-flex px-2 py-1 text-xs rounded-full mt-1 ${getTypeColor(platform.type)}`}>
                          {getTypeLabel(platform.type)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {platform.accountNumber && (
                    <div className="mb-4 text-sm text-gray-600">
                      <span className="font-medium">Account:</span> {platform.accountNumber}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="py-3 px-4 bg-blue-50 rounded-xl">
                      <div className="text-xs text-blue-700 mb-1">Wallet Balance</div>
                      <div className="text-lg font-bold text-blue-600">
                        {formatCurrency(platform.walletBalance || 0)}
                      </div>
                    </div>
                    <div className="py-3 px-4 bg-green-50 rounded-xl">
                      <div className="text-xs text-green-700 mb-1">Total Invested</div>
                      <div className="text-lg font-bold text-green-600">
                        {formatCurrency(platform.totalInvested || 0)}
                      </div>
                    </div>
                  </div>

                  {platform.notes && (
                    <div className="mb-4 text-sm text-gray-600 line-clamp-2">
                      {platform.notes}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Link
                      to={`/investments/platform/${platform._id}`}
                      className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <Eye className="w-4 h-4" />
                      View Investments
                    </Link>
                    <button
                      onClick={() => handleEdit(platform)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(platform._id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PageTransition>
  );
};

export default InvestmentPlatforms;
